/**
 * BI targets (metas) - D531. Pure: the rules of a goal for a metric in a market, shared by the API (which refuses what does not pass), the fake API and the
 * Admin form (which shows the same reason before it asks). A goal is a plain number in the metric's own unit; money is excluded (an amount needs a currency
 * and a goal per currency is a different feature).
 *
 * Which side of the goal is good is part of the goal (`lowerIsBetter`), defaulted from the metric by {@link defaultLowerIsBetter}: a share of pickups on time
 * is better higher, a time to match is better lower.
 */
import { METRICS, getMetric } from './catalog.js';
/** The units a goal can be set in (a quotient of amounts is a `ratio`: a share of money is fine, an amount is not). */
export const TARGET_UNITS = ['count', 'ratio', 'duration_s', 'decimal', 'delta_s'];
/** The inclusive range of a goal's value per unit, in the unit itself (a share as a fraction, a duration in seconds). */
export const TARGET_RANGES = {
    count: [0, 1_000_000_000],
    ratio: [0, 1],
    duration_s: [0, 604_800],
    decimal: [0, 1000],
    delta_s: [-86_400, 86_400],
};
const MARKET_ID = /^[a-z]{2}:[a-z0-9]+(?:-[a-z0-9]+)*$/;
const METRIC_ID = /^[a-z][a-z0-9_]{0,63}$/;
/** Metrics where a LOWER figure is the good side: times, waits, rates of things that go wrong. Everything else is better higher. */
const LOWER_IS_BETTER = new Set([
    'cancellation_rate', 'delivery_time', 'prep_time', 'time_to_assign', 'accept_time', 'pickup_time', 'trip_time', 'eta_deviation', 'offer_decline_rate', 'offer_lapse_rate',
    'low_rating_share', 'store_pauses', 'store_paused_seconds', 'promo_discount_rate', 'rides_no_driver',
]);
export const defaultLowerIsBetter = (metricId) => LOWER_IS_BETTER.has(metricId);
/** The metrics a goal can be set for: every catalog metric whose unit is a plain number, a share or a duration. */
export function targetableMetrics() {
    return METRICS.filter((m) => TARGET_UNITS.includes(m.unit)).map((m) => m.id);
}
const isObject = (v) => typeof v === 'object' && v !== null && !Array.isArray(v);
const round6 = (n) => Math.round(n * 1e6) / 1e6;
const KEYS = new Set(['metric', 'marketId', 'value', 'bandLow', 'bandHigh', 'lowerIsBetter']);
/** Validate an untrusted goal. The value is rounded to six decimals (what the table keeps). */
export function vetTarget(raw) {
    if (!isObject(raw) || Object.keys(raw).some((k) => !KEYS.has(k)))
        return { ok: false, code: 'TARGET_BODY_INVALID' };
    if (typeof raw.metric !== 'string' || !METRIC_ID.test(raw.metric))
        return { ok: false, code: 'TARGET_METRIC_UNKNOWN', field: 'metric' };
    const metric = getMetric(raw.metric);
    if (metric === undefined)
        return { ok: false, code: 'TARGET_METRIC_UNKNOWN', field: 'metric' };
    if (!TARGET_UNITS.includes(metric.unit))
        return { ok: false, code: 'TARGET_METRIC_UNIT_UNSUPPORTED', field: 'metric' };
    if (typeof raw.marketId !== 'string' || !MARKET_ID.test(raw.marketId) || raw.marketId.length > 64)
        return { ok: false, code: 'TARGET_MARKET_INVALID', field: 'marketId' };
    const [lo, hi] = TARGET_RANGES[metric.unit];
    const inRange = (n) => typeof n === 'number' && Number.isFinite(n) && n >= lo && n <= hi;
    if (!inRange(raw.value))
        return { ok: false, code: 'TARGET_VALUE_INVALID', field: 'value' };
    const value = round6(raw.value);
    const hasLow = raw.bandLow !== undefined && raw.bandLow !== null;
    const hasHigh = raw.bandHigh !== undefined && raw.bandHigh !== null;
    if (hasLow !== hasHigh)
        return { ok: false, code: 'TARGET_BAND_INVALID', field: hasLow ? 'bandHigh' : 'bandLow' };
    let bandLow = null;
    let bandHigh = null;
    if (hasLow && hasHigh) {
        if (!inRange(raw.bandLow) || !inRange(raw.bandHigh))
            return { ok: false, code: 'TARGET_BAND_INVALID', field: 'bandLow' };
        bandLow = round6(raw.bandLow);
        bandHigh = round6(raw.bandHigh);
        if (!(bandLow <= value && value <= bandHigh))
            return { ok: false, code: 'TARGET_BAND_INVALID', field: 'bandLow' };
    }
    if (raw.lowerIsBetter !== undefined && typeof raw.lowerIsBetter !== 'boolean')
        return { ok: false, code: 'TARGET_BODY_INVALID', field: 'lowerIsBetter' };
    return { ok: true, value: { metric: metric.id, marketId: raw.marketId, value, bandLow, bandHigh, lowerIsBetter: raw.lowerIsBetter ?? defaultLowerIsBetter(metric.id) } };
}
/** `metric` + `marketId` of a goal to clear (a delete names both). */
export function vetTargetKey(raw) {
    if (typeof raw.metric !== 'string' || !METRIC_ID.test(raw.metric) || getMetric(raw.metric) === undefined)
        return { ok: false, code: 'TARGET_METRIC_UNKNOWN', field: 'metric' };
    if (typeof raw.marketId !== 'string' || !MARKET_ID.test(raw.marketId) || raw.marketId.length > 64)
        return { ok: false, code: 'TARGET_MARKET_INVALID', field: 'marketId' };
    return { ok: true, metric: raw.metric, marketId: raw.marketId };
}
/**
 * Where a measured figure stands against a goal: `reached` on the good side of the goal (or equal), `near` inside the accepted band, `missed` otherwise.
 * Without a band, anything on the wrong side is `missed`. The band only ever widens what counts as `near`.
 */
export function targetStatus(value, target) {
    const good = target.lowerIsBetter ? value <= target.value : value >= target.value;
    if (good)
        return 'reached';
    if (target.bandLow !== null && target.bandHigh !== null && value >= target.bandLow && value <= target.bandHigh)
        return 'near';
    return 'missed';
}
//# sourceMappingURL=targets.js.map